import { useEffect, useRef } from 'react';

const DIRECTION_LOCK_THRESHOLD = 10; // px of movement before locking axis

/**
 * Touch swipe hook that provides real-time finger tracking during drag,
 * direction-locked swipe detection, velocity-aware completion animations,
 * and snap-back. Fully owns the touch animation lifecycle via direct DOM
 * manipulation, then syncs React state after the animation finishes.
 *
 * Key invariant: isTransitioning stays false throughout the entire swipe
 * lifecycle, so React always renders transition: 'none' on the container
 * and never overwrites the hook's DOM-level transition.
 */
export const useTouchSwipe = ({
  containerRef,
  currentTransformIndex,
  activeSlideIndex,
  totalSlides,
  isTransitioning,
  isMobile,
  slidesFullControlsEnabled,
  isVideoFullMode,
  isAutoPlaying,
  prefersReducedMotion,
  transitionDuration,
  onSwipeComplete,
  onDragStart,
  onDragEnd,
}) => {
  // Store latest props in a ref so event listeners always read current values
  // without needing to re-attach on every render.
  const propsRef = useRef();
  propsRef.current = {
    activeSlideIndex,
    currentTransformIndex,
    isAutoPlaying,
    isMobile,
    isTransitioning,
    isVideoFullMode,
    onDragEnd,
    onDragStart,
    onSwipeComplete,
    prefersReducedMotion,
    slidesFullControlsEnabled,
    totalSlides,
    transitionDuration,
  };

  const dragState = useRef({
    isAnimating: false,
    isDragging: false,
    isHorizontal: null, // null = undetermined, true/false = locked
    startTime: 0,
    startX: 0,
    startY: 0,
    wasAutoPlaying: false,
  });

  const safetyTimeoutRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const clearSafetyTimeout = () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    };

    const getTransformValue = (index, offsetPx = 0) => {
      if (offsetPx === 0) return `translateX(-${index * 100}%)`;
      return `translateX(calc(-${index * 100}% + ${offsetPx}px))`;
    };

    // ── touchstart ──────────────────────────────────────────────────────
    const handleTouchStart = e => {
      const props = propsRef.current;
      const state = dragState.current;

      if (props.isVideoFullMode) return;
      if (!props.isMobile && props.slidesFullControlsEnabled) return;
      if (props.isTransitioning || state.isAnimating) return;
      if (props.totalSlides <= 1) return;

      const touch = e.touches?.[0];
      if (!touch) return;

      state.startX = touch.clientX;
      state.startY = touch.clientY;
      state.startTime = performance.now();
      state.isDragging = true;
      state.isHorizontal = null;
      state.wasAutoPlaying = props.isAutoPlaying;

      if (props.isAutoPlaying) {
        props.onDragStart();
      }
    };

    // ── touchmove ───────────────────────────────────────────────────────
    const handleTouchMove = e => {
      const state = dragState.current;
      if (!state.isDragging) return;

      const touch = e.touches?.[0];
      if (!touch) return;

      const diffX = touch.clientX - state.startX;
      const diffY = touch.clientY - state.startY;

      // Direction lock: wait for enough movement to decide
      if (state.isHorizontal === null) {
        if (
          Math.sqrt(diffX * diffX + diffY * diffY) < DIRECTION_LOCK_THRESHOLD
        ) {
          return;
        }

        state.isHorizontal = Math.abs(diffX) > Math.abs(diffY);
        if (!state.isHorizontal) {
          // Vertical gesture — release to browser for native scrolling
          state.isDragging = false;
          if (state.wasAutoPlaying) {
            propsRef.current.onDragEnd();
          }
          return;
        }
      }

      // Horizontal gesture — claim it
      e.preventDefault();

      if (propsRef.current.prefersReducedMotion) return;

      // 60fps finger tracking via direct DOM manipulation
      container.style.transition = 'none';
      container.style.transform = getTransformValue(
        propsRef.current.currentTransformIndex,
        diffX,
      );
    };

    // ── touchend ────────────────────────────────────────────────────────
    const handleTouchEnd = e => {
      // Snapshot props at decision time so completion callbacks use
      // consistent values even if React re-renders during the animation.
      const props = propsRef.current;
      const state = dragState.current;

      if (!state.isDragging) return;
      state.isDragging = false;

      const touch = e.changedTouches?.[0];
      if (!touch) {
        if (!props.prefersReducedMotion) {
          container.style.transition = 'none';
          container.style.transform = getTransformValue(
            props.currentTransformIndex,
          );
        }
        if (state.wasAutoPlaying) props.onDragEnd();
        return;
      }

      const diffX = touch.clientX - state.startX;
      const absDiffX = Math.abs(diffX);
      const absDiffY = Math.abs(touch.clientY - state.startY);
      const elapsedTime = performance.now() - state.startTime;

      // Swipe detection thresholds (mirrored from the previous implementation)
      const viewportWidth = window.innerWidth;
      const swipeThreshold = Math.max(30, Math.min(50, viewportWidth * 0.1));
      const velocity = absDiffX / elapsedTime; // px/ms
      const isQuickFlick = velocity > 0.5 && absDiffX > 20;
      const isHorizontalSwipe = absDiffX > absDiffY;
      const meetsThreshold = absDiffX > swipeThreshold;

      const shouldNavigate =
        state.isHorizontal &&
        isHorizontalSwipe &&
        (meetsThreshold || isQuickFlick);

      // Negative diffX = finger moved left = show next slide
      const direction = diffX < 0 ? 'next' : 'prev';

      if (shouldNavigate) {
        if (props.prefersReducedMotion) {
          // Instant navigation — React renders the new position with transition: 'none'
          props.onSwipeComplete(direction);
          return;
        }

        // ── Animated completion ───────────────────────────────────────
        state.isAnimating = true;

        const targetIndex =
          direction === 'next'
            ? props.currentTransformIndex + 1
            : props.currentTransformIndex - 1;

        const dragFraction = Math.min(1, absDiffX / viewportWidth);
        const scaledDuration = Math.max(
          200,
          props.transitionDuration * (1 - dragFraction),
        );

        let navigateCleanedUp = false;
        const cleanUpNavigate = () => {
          if (navigateCleanedUp) return;
          navigateCleanedUp = true;
          clearSafetyTimeout();
          // eslint-disable-next-line no-use-before-define
          container.removeEventListener('transitionend', onNavigateEnd);
          state.isAnimating = false;
          // Clear the hook's transition BEFORE the state update so React's
          // re-render doesn't trigger a new CSS transition when it sets the
          // transform to the real slide position (clone → real snap).
          container.style.transition = 'none';
          props.onSwipeComplete(direction);
        };

        const onNavigateEnd = evt => {
          if (evt.target !== container || evt.propertyName !== 'transform')
            return;
          cleanUpNavigate();
        };

        container.addEventListener('transitionend', onNavigateEnd);
        safetyTimeoutRef.current = setTimeout(
          cleanUpNavigate,
          scaledDuration + 100,
        );

        container.style.transition = `transform ${scaledDuration}ms ease-out`;
        container.style.transform = getTransformValue(targetIndex);
      } else if (!props.prefersReducedMotion && state.isHorizontal) {
        // ── Snap back ─────────────────────────────────────────────────
        state.isAnimating = true;
        const snapDuration = Math.min(300, Math.max(150, absDiffX * 2));

        let snapCleanedUp = false;
        const cleanUpSnap = () => {
          if (snapCleanedUp) return;
          snapCleanedUp = true;
          clearSafetyTimeout();
          // eslint-disable-next-line no-use-before-define
          container.removeEventListener('transitionend', onSnapEnd);
          state.isAnimating = false;
          container.style.transition = '';
          if (state.wasAutoPlaying) props.onDragEnd();
        };

        const onSnapEnd = evt => {
          if (evt.target !== container || evt.propertyName !== 'transform')
            return;
          cleanUpSnap();
        };

        container.addEventListener('transitionend', onSnapEnd);
        safetyTimeoutRef.current = setTimeout(cleanUpSnap, snapDuration + 100);

        container.style.transition = `transform ${snapDuration}ms ease-out`;
        container.style.transform = getTransformValue(
          props.currentTransformIndex,
        );
      } else if (state.wasAutoPlaying) {
        // No horizontal drag occurred, or reduced motion — just resume autoplay
        props.onDragEnd();
      }
    };

    // ── touchcancel ─────────────────────────────────────────────────────
    const handleTouchCancel = () => {
      const state = dragState.current;
      if (!state.isDragging) return;
      state.isDragging = false;

      // Snap back to current position immediately (no animation)
      container.style.transition = 'none';
      container.style.transform = getTransformValue(
        propsRef.current.currentTransformIndex,
      );

      if (state.wasAutoPlaying) {
        propsRef.current.onDragEnd();
      }
    };

    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchCancel, {
      passive: true,
    });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      clearSafetyTimeout();
    };
    // Listeners read current values from propsRef — only re-attach if the
    // container element itself changes (which it shouldn't).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);
};
